<template>
  <BaseCard
    :heading="$t('insights.appointments_heading')"
    :loading="loading"
  >
    <div
      :class="[$style.base, { [$style.smallScreen]: $screen === 's', [$style.mediumScreen]: $screen === 'm' }]"
    >
      <div :class="$style.main">
        <Heatmap :data="formattedData" />
      </div>
      <Flex
        direction="column"
        :gap="2"
        :class="$style.side"
      >
        <Flex :gap="2">
          <Flex
            v-if="busiestPart"
            direction="column"
            :gap="0.5"
            v-test="'insights-busiest-time'"
          >
            <BaseHeading size="xs">
              {{ $t('insights.busiest_time') }}
            </BaseHeading>
            <div>
              <BaseText bold>
                {{ filters.date(busiestPart.date, {
                  format: 'long'
                }) }}
              </BaseText>
              <BaseText bold>{{ formatTime(busiestPart.part) }}</BaseText>
              <BaseText size="s">
                {{ busiestPart.sum }}
                {{ $t('global.items.appointment', busiestPart.sum) }}
              </BaseText>
            </div>
          </Flex>
          <Flex
            v-if="leastBusyPart"
            direction="column"
            :gap="0.5"
            v-test="'insights-least-busy-time'"
          >
            <BaseHeading size="xs">
              {{ $t('insights.most_available') }}
            </BaseHeading>
            <div>
              <BaseText bold>
                {{ filters.date(leastBusyPart.date, {
                  format: 'long'
                }) }}
              </BaseText>
              <BaseText bold>{{ formatTime(leastBusyPart.part) }}</BaseText>
              <BaseText size="s">
                {{ leastBusyPart.sum }}
                {{ $t('global.items.appointment', leastBusyPart.sum) }}
              </BaseText>
            </div>
          </Flex>
        </Flex>
        <BaseCard gray>
          <Flex direction="column">
            <BaseLabel color="success">{{ $t('insights.pro_tip') }}</BaseLabel>
            <BaseHeading>
              {{ $t('insights.dynamic_pricing.heading') }}
            </BaseHeading>
            <BaseText
              :routerLink="{ name: 'marketing-discounts' }"
              v-test="'insights-dynamic-pricing-link'"
            >
              {{ $t('insights.dynamic_pricing.button_text') }}
            </BaseText>
          </Flex>
        </BaseCard>
      </Flex>
    </div>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import filters from '@/filters';
import { useLocationsStore } from '@/stores/locations';
import { GET_HEATMAP_INSIGHTS } from './graphql';
import Heatmap from './Heatmap.vue';
import { formatTime } from './helpers';
import type { HeatMapInsight } from './types';

const { locationId, dataScope } = storeToRefs(useLocationsStore());
const startAt = inject<Ref<string>>('startAt')!;
const endAt = inject<Ref<string>>('endAt')!;

const emit = defineEmits(['loaded']);

const { result, loading, onResult } = useQuery(GET_HEATMAP_INSIGHTS, () => ({
  dataScope: dataScope.value,
  locationId: locationId.value,
  startAt: startAt.value,
  endAt: endAt.value,
}));

const data = computed<HeatMapInsight[]>(
  () => result.value?.heatMapInsightsV2 || [],
);
const formattedData = computed(() => {
  const EARLY_PARTS = ['00-03', '03-06', '06-09'];
  const LATE_PART = '21-24';

  return Object.values(
    data.value.reduce(
      (
        byDate: Record<string, { date: string; parts: Record<string, number> }>,
        entry: HeatMapInsight,
      ) => {
        const { date, part, sum } = entry;

        if (!byDate[date]) {
          byDate[date] = { date, parts: {} };
        }

        byDate[date].parts[part] = (byDate[date].parts[part] || 0) + sum;
        return byDate;
      },
      {},
    ),
  ).flatMap(({ date, parts }) => {
    // add early parts to 09-12
    const earlySum = EARLY_PARTS.reduce((acc, p) => acc + (parts[p] || 0), 0);
    if (earlySum) {
      parts['09-12'] = (parts['09-12'] || 0) + earlySum;
    }
    // remove early parts
    EARLY_PARTS.forEach((p) => {
      delete parts[p];
    });

    // move 21-24 to 18-21 (even if it's 0, we still remove 21-24)
    if (LATE_PART in parts) {
      parts['18-21'] = (parts['18-21'] || 0) + (parts[LATE_PART] || 0);
      delete parts[LATE_PART];
    }

    // back to array
    return Object.entries(parts).map(([part, sum]) => ({
      date,
      part,
      sum,
    }));
  });
});

const highestSum = computed(() =>
  Math.max(...formattedData.value.map((obj) => obj.sum)),
);

const lowestSum = computed(() =>
  Math.min(...formattedData.value.map((obj) => obj.sum)),
);

const busiestParts = computed(() =>
  formattedData.value.filter((obj) => obj.sum === highestSum.value),
);
const leastBusyParts = computed(() =>
  formattedData.value.filter((obj) => obj.sum === lowestSum.value),
);

const busiestPart = computed(() => busiestParts.value[0]);
const leastBusyPart = computed(() => leastBusyParts.value[0]);

onResult(() => {
  const totalAppointments = formattedData.value.reduce(
    (acc, obj) => acc + obj.sum,
    0,
  );
  emit('loaded', totalAppointments);
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  gap: $spacing * 2;

  &.smallScreen, &.mediumScreen {
    flex-direction: column;
  }
}

.main {
  flex: 6 1 0;
}

.side {
  flex: 4 1 0;
}
</style>
